import React, { useState, useEffect } from 'react';
import './GameCallUpModal.css';
import API_BASE_URL from '../../config/api';

const GameCallUpModal = ({ isOpen, onClose, event, teamId }) => {
    const [athletes, setAthletes] = useState([]);
    const [selectedAthletes, setSelectedAthletes] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (isOpen && event && teamId) {
            fetchData();
        }
    }, [isOpen, event, teamId]);

    if (!isOpen || !event || event.eventType !== 1) return null;

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        setSuccessMessage('');
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [teamRes, callUpRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/teams/${teamId}`, { headers }),
                fetch(`${API_BASE_URL}/api/game-callups/${event.id}`, { headers }),
            ]);

            if (!teamRes.ok) throw new Error('Falha ao carregar equipa.');
            const teamData = await teamRes.json();

            let calledUpIds = [];
            if (callUpRes.ok) {
                calledUpIds = await callUpRes.json();
            }

            setAthletes(teamData.athletes || []);
            setSelectedAthletes(new Set(calledUpIds));

        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const isPastEvent = event && new Date(event.startDateTime) < new Date();

    const toggleAthlete = (athleteId) => {
        if (isPastEvent) return;
        const newSelected = new Set(selectedAthletes);
        if (newSelected.has(athleteId)) {
            newSelected.delete(athleteId);
        } else {
            newSelected.add(athleteId);
        }
        setSelectedAthletes(newSelected);
    };

    const handleSelectAll = () => {
        if (isPastEvent) return;
        if (selectedAthletes.size === athletes.length) {
            setSelectedAthletes(new Set());
        } else {
            setSelectedAthletes(new Set(athletes.map(a => a.id)));
        }
    };

    const handleSave = async () => {
        if (isPastEvent) return;
        setSaving(true);
        setError(null);
        setSuccessMessage('');
        try {
            const token = localStorage.getItem('token');
            const payload = {
                eventId: event.id,
                athleteIds: Array.from(selectedAthletes)
            };

            const response = await fetch(`${API_BASE_URL}/api/game-callups`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error('Falha ao guardar convocatória.');

            const result = await response.json();
            setSuccessMessage(`Convocatória guardada! ${result.addedCount} atletas notificados.`);

            setTimeout(() => {
                onClose();
            }, 2500);

        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const formatDate = dt =>
        new Date(dt).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' });

    const formatTime = dt =>
        new Date(dt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });

    const getInitials = athlete =>
        athlete.jerseyNumber
            ? `#${athlete.jerseyNumber}`
            : athlete.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className={`game-callup-modal ${isPastEvent ? 'is-past' : ''}`} onClick={e => e.stopPropagation()}>
                <header className="gcm-header">
                    <h2>
                        <i className={`fas ${isPastEvent ? 'fa-lock' : 'fa-bullhorn'}`} />
                        {isPastEvent ? 'Convocatória Fechada' : 'Convocatória para Jogo'}
                    </h2>
                    <button className="gcm-close-btn" onClick={onClose} aria-label="Fechar">
                        &times;
                    </button>
                </header>

                <div className="gcm-body">
                    <div className="gcm-event-card">
                        <div className="gcm-event-info">
                            <h3>{event.title}</h3>
                            <div className="gcm-event-meta">
                                <span><i className="fas fa-calendar-alt" /> {formatDate(event.startDateTime)}</span>
                                <span><i className="fas fa-clock" /> {formatTime(event.startDateTime)}</span>
                                <span><i className="fas fa-map-marker-alt" /> {event.location || 'Local a definir'}</span>
                            </div>
                        </div>
                        {isPastEvent && (
                            <div className="gcm-past-badge">
                                <i className="fas fa-history" /> Jogo Realizado
                            </div>
                        )}
                    </div>

                    {isPastEvent && !successMessage && (
                        <div className="gcm-lock-warning">
                            <i className="fas fa-info-circle" /> O jogo já se realizou. A convocatória está em modo de visualização.
                        </div>
                    )}

                    {successMessage ? (
                        <div className="gcm-success-pane">
                            <i className="fas fa-check-circle gcm-success-icon" />
                            <p style={{ fontWeight: 800, color: '#10b981', margin: 0, fontSize: '1.2rem' }}>{successMessage}</p>
                        </div>
                    ) : (
                        <>
                            {!isPastEvent && (
                                <div className="gcm-selection-bar">
                                    <button className="gcm-select-all" onClick={handleSelectAll}>
                                        {selectedAthletes.size === athletes.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                                    </button>
                                    <div className="gcm-count-badge">
                                        {selectedAthletes.size} Selecionados
                                    </div>
                                </div>
                            )}

                            <div className="gcm-list">
                                {loading ? (
                                    <div className="loading-spinner">A carregar atletas...</div>
                                ) : (
                                    athletes.map(athlete => {
                                        const isSelected = selectedAthletes.has(athlete.id);
                                        return (
                                            <div
                                                key={athlete.id}
                                                className={`gcm-row ${isSelected ? 'selected' : ''} ${isPastEvent ? 'disabled' : ''}`}
                                                onClick={() => toggleAthlete(athlete.id)}
                                            >
                                                <div className="gcm-avatar">
                                                    {getInitials(athlete)}
                                                </div>
                                                <div className="gcm-athlete-name">
                                                    {athlete.name}
                                                </div>
                                                <div className="gcm-checkbox">
                                                    {isSelected && <i className="fas fa-check" />}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </>
                    )}
                </div>

                <footer className="gcm-footer">
                    <button className="gcm-btn gcm-btn-cancel" onClick={onClose} disabled={saving}>
                        {isPastEvent ? 'Fechar' : 'Cancelar'}
                    </button>
                    {!successMessage && !isPastEvent && (
                        <button
                            className="gcm-btn gcm-btn-primary"
                            onClick={handleSave}
                            disabled={saving || loading || selectedAthletes.size === 0}
                        >
                            {saving ? 'A guardar...' : 'Guardar e Notificar'}
                        </button>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default GameCallUpModal;
